/**
 * Headless customer-display subscription (design-agnostic).
 *
 * Short-polls the PUBLIC, read-only display-state endpoint for a station,
 * authenticated only by the signed display ``token`` in the URL. Returns the
 * latest mirror payload (idle / building / card / settled) for the UI to dress.
 * No session, no mutation — a dumb second screen pointed at ``/pos/display?token=``.
 *
 * (Transport is a ~1 s short-poll today; an SSE push could replace it later
 * without changing this hook's surface or the display component.)
 */

import { useEffect, useRef, useState } from "react";

import { ApiError, api, type PosDisplayState } from "../../api/client";

export function usePosDisplay(
  token: string | null,
  pollMs = 1000,
): { state: PosDisplayState | null; error: string | null } {
  const [state, setState] = useState<PosDisplayState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!token) {
      setError("missing display token");
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const s = await api.posDisplayState(token);
        if (!alive.current) return;
        setState(s);
        setError(null);
      } catch (e) {
        if (!alive.current) return;
        // Keep the last good frame on a transient blip; surface a hard error.
        setError(e instanceof ApiError ? e.detail : "display unavailable");
      } finally {
        if (alive.current) timer = setTimeout(() => void tick(), pollMs);
      }
    };
    void tick();

    return () => {
      alive.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [token, pollMs]);

  return { state, error };
}
